'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Clock } from 'lucide-react';
import { BLOG_POSTS } from '../data/blogs';

export default function BlogSection() {
  const router = useRouter();
  const featured = BLOG_POSTS?.find((p) => p?.featured);
  const rest = BLOG_POSTS?.filter((p) => !p?.featured)?.slice(0, 4);

  return (
    <section className="py-14" style={{ background: '#FBF8F3' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-display font-600 text-primary-500 uppercase tracking-widest mb-1" style={{ fontWeight: 600 }}>
              Travel Stories
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-800 text-gray-900" style={{ fontWeight: 800 }}>
              Inspiration & Guides
            </h2>
          </div>
          <button
            onClick={() => router?.push('/blog')}
            className="hidden sm:flex items-center gap-1.5 text-sm font-display font-600 text-primary-500 hover:text-primary-600 transition-colors duration-150"
            style={{ fontWeight: 600 }}>
            All articles <ArrowRight size={14} />
          </button>
        </div>

        {/* Layout: featured left + 4 smaller right */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5">
          {/* Featured post */}
          {featured && (
            <div
              onClick={() => router?.push(`/blog/${featured?.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e?.key === 'Enter' && router?.push(`/blog/${featured?.id}`)}
              aria-label={`Read: ${featured?.title}`}
              className="lg:col-span-3 group relative rounded-2xl overflow-hidden cursor-pointer card-shadow hover:card-shadow-lg transition-all duration-300 active:scale-[0.99]"
              style={{ minHeight: 280 }}
            >
              <img
                src={featured?.image}
                alt={featured?.alt}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-display font-600 text-white mb-2 sm:mb-3 ${featured?.categoryColor}`} style={{ fontWeight: 600 }}>
                  {featured?.category}
                </span>
                <h3 className="font-display font-800 text-white text-lg sm:text-2xl leading-snug mb-1 sm:mb-2" style={{ fontWeight: 800 }}>
                  {featured?.title}
                </h3>
                <p className="text-white/75 text-sm font-body leading-relaxed mb-3 sm:mb-4 line-clamp-2 hidden sm:block">
                  {featured?.excerpt}
                </p>
                <div className="flex items-center gap-2 sm:gap-3 text-white/60 text-xs font-body flex-wrap">
                  <span>{featured?.author}</span>
                  <span>·</span>
                  <Clock size={11} />
                  <span>{featured?.readTime}</span>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline">{featured?.date}</span>
                </div>
              </div>
            </div>
          )}

          {/* Smaller posts */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
            {rest?.map((post) => (
              <div
                key={post?.id}
                onClick={() => router?.push(`/blog/${post?.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e?.key === 'Enter' && router?.push(`/blog/${post?.id}`)}
                aria-label={`Read: ${post?.title}`}
                className="group flex gap-3 sm:gap-4 bg-white rounded-2xl overflow-hidden card-shadow hover:card-shadow-lg transition-all duration-300 cursor-pointer p-3 active:scale-[0.98]"
              >
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-xl overflow-hidden">
                  <img
                    src={post?.image}
                    alt={post?.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0 py-0.5">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-display font-600 text-white mb-1.5 ${post?.categoryColor}`} style={{ fontWeight: 600 }}>
                    {post?.category}
                  </span>
                  <h3 className="font-display font-700 text-gray-900 text-xs sm:text-sm leading-snug line-clamp-2 mb-1.5" style={{ fontWeight: 700 }}>
                    {post?.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-400 text-xs font-body">
                    <Clock size={10} />
                    <span>{post?.readTime}</span>
                    <span>·</span>
                    <span>{post?.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile "all articles" link */}
        <div className="mt-6 sm:hidden text-center">
          <button
            onClick={() => router?.push('/blog')}
            className="inline-flex items-center gap-1.5 text-sm font-display font-600 text-primary-500 min-h-[44px]"
            style={{ fontWeight: 600 }}>
            All articles <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
